import React, { useState, useEffect } from 'react';
import { PhotoFeatureProperties } from '../../services/mapGeoJson';
import { storageService } from '../../services/storage';
import { Calendar, MapPin, Maximize2, X, Tag } from 'lucide-react';

export interface PhotoPopupProps {
  photo: PhotoFeatureProperties;
  onExpand: (id: string) => void;
  onClose: () => void;
}

export const PhotoPopup: React.FC<PhotoPopupProps> = ({ photo, onExpand, onClose }) => {
  const [thumbSrc, setThumbSrc] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);

  // Asynchronously resolve thumbnail URL (from direct URL or hybrid storage)
  useEffect(() => {
    let isCancelled = false;
    const initialUrl = photo.thumbnailUrl;

    if (
      initialUrl &&
      (initialUrl.startsWith('http://') ||
        initialUrl.startsWith('https://') ||
        initialUrl.startsWith('data:') ||
        initialUrl.startsWith('blob:'))
    ) {
      setThumbSrc(initialUrl);
      setIsImageLoading(false);
      return;
    }

    const keyToResolve = photo.thumbnailKey || photo.thumbnailUrl;
    if (!keyToResolve) {
      setThumbSrc(null);
      setIsImageLoading(false);
      return;
    }

    setIsImageLoading(true);
    storageService
      .getPhotoUrl(keyToResolve)
      .then((resolved) => {
        if (!isCancelled) {
          setThumbSrc(resolved || null);
          setIsImageLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setThumbSrc(null);
          setIsImageLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [photo.id, photo.thumbnailUrl, photo.thumbnailKey]);

  // Category badge color mapping - High contrast & vibrant saturated colors
  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'Con me':
      case 'Foto con me':
        return 'bg-rose-600 text-white border-rose-300/80 shadow-md shadow-rose-950/60';
      case 'Panorami':
      case 'Paesaggi':
      case 'Landscape':
        return 'bg-sky-600 text-white border-sky-300/80 shadow-md shadow-sky-950/60';
      case 'Dettagli':
      case 'Details':
        return 'bg-emerald-600 text-white border-emerald-300/80 shadow-md shadow-emerald-950/60';
      case 'Cibo':
      case 'Food':
        return 'bg-amber-600 text-white border-amber-300/80 shadow-md shadow-amber-950/60';
      case 'Architettura':
      case 'Architecture':
        return 'bg-indigo-600 text-white border-indigo-300/80 shadow-md shadow-indigo-950/60';
      default:
        return 'bg-blue-600 text-white border-blue-300/80 shadow-md shadow-blue-950/60';
    }
  };

  const formattedDate = React.useMemo(() => {
    if (!photo.timestamp) return 'Data non disponibile';
    try {
      const d = new Date(photo.timestamp * 1000);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data non disponibile';
    }
  }, [photo.timestamp]);

  return (
    <div className="w-72 bg-slate-900/95 backdrop-blur-md border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col font-sans transition-all animate-in fade-in zoom-in-95 duration-150">
      {/* Thumbnail Area */}
      <div
        className="relative w-full h-44 bg-slate-950 overflow-hidden group cursor-pointer"
        onClick={() => onExpand(photo.id)}
      >
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt={photo.category || photo.id}
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
              isImageLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}
            loading="eager"
            onLoad={() => setIsImageLoading(false)}
          />
        ) : isImageLoading ? (
          <div className="w-full h-full bg-slate-900 animate-pulse flex flex-col items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
            <span className="text-[11px] text-slate-400 font-mono">Caricamento miniatura...</span>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-xs gap-1">
            <MapPin className="w-6 h-6 stroke-1 text-slate-600" />
            <span>Nessuna miniatura</span>
          </div>
        )}

        {/* Category Pill Tag (High-contrast label) */}
        <div className="absolute top-2.5 left-2.5">
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold border flex items-center gap-1 shadow-md ${getCategoryColor(
              photo.category
            )}`}
          >
            <Tag className="w-3 h-3 text-white shrink-0" />
            <span className="tracking-wide">{photo.category || 'Generale'}</span>
          </span>
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-slate-950/80 hover:bg-slate-900 border border-slate-600/70 flex items-center justify-center text-slate-200 hover:text-white transition shadow-md cursor-pointer backdrop-blur-sm"
          aria-label="Chiudi popup"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Quick Expand Button Overlay on Hover */}
        <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <span className="px-3 py-1 bg-slate-900/80 backdrop-blur rounded-lg text-xs font-medium text-white flex items-center gap-1.5 shadow-lg border border-white/10">
            <Maximize2 className="w-3.5 h-3.5" />
            <span>Ingrandisci</span>
          </span>
        </div>
      </div>

      {/* Metadata & Actions Area */}
      <div className="p-3.5 flex flex-col gap-2">
        {/* Step Location or Category Label (Filename eliminated) */}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-100 truncate">
          <MapPin className="w-3.5 h-3.5 text-sky-400 shrink-0" />
          <span className="truncate">{photo.stepName || photo.category || 'Punto sulla mappa'}</span>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-1.5 text-[11px] text-slate-300 font-medium">
          <Calendar className="w-3.5 h-3.5 text-sky-400/80 shrink-0" />
          <span>{formattedDate}</span>
        </div>

        {/* Action Button: Level 2 Lightbox */}
        <button
          type="button"
          onClick={() => onExpand(photo.id)}
          className="w-full mt-1.5 py-2 px-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-semibold rounded-xl flex items-center justify-center gap-2 transition shadow-lg shadow-blue-500/20 active:scale-[0.98] cursor-pointer"
        >
          <Maximize2 className="w-3.5 h-3.5" />
          <span>Visualizza a Schermo Intero</span>
        </button>
      </div>
    </div>
  );
};
